import { Button, Card, EmptyState, formatDate, Icon, Skeleton, useToast } from "@helmr/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { listBills, type BillSummary } from "../../api/billing";
import { isApiError } from "../../api/client";
import { NoAccess } from "../../app/NoAccess";
import { areaAllows } from "../../app/nav";
import { today } from "../../lib/dates";
import { isUnmasked, whatsappHref } from "../../lib/phone";
import { useAppContext, useMoney, useScope } from "../billing/hooks";
import { PageHead, QueryFailure } from "../billing/states";
import { billing } from "../billing/strings";
import s from "../billing/billing.module.css";

const t = billing.dues;
const CAP = 200;

/** Whole days from `a` to `b` (both `YYYY-MM-DD`), counted on UTC calendar days so no zone or DST can move it (rule 3). */
export function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number) as [number, number, number];
  const [by, bm, bd] = b.split("-").map(Number) as [number, number, number];
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

export type Bucket = "b0" | "b1" | "b2" | "notDue" | "noDate";
export function bucketOf(dueDate: string | null, todayYmd: string): Bucket {
  if (!dueDate) return "noDate";
  const late = daysBetween(dueDate, todayYmd);
  return late < 0 ? "notDue" : late <= 7 ? "b0" : late <= 30 ? "b1" : "b2";
}
const ORDER: Bucket[] = ["b2", "b1", "b0", "notDue", "noDate"];

/** A bill with no instalment schedule has no `dueDate` and is due in full on its business date, as `GET /reports/dues` ages it. */
export const dueOf = (b: Pick<BillSummary, "dueDate" | "businessDate">): string => b.dueDate ?? b.businessDate;

export function DuesPage() {
  const ctx = useAppContext();
  const { client, now, scopeKey } = useScope();
  const { fmt } = useMoney(ctx);
  const toast = useToast();
  const day = today(now(), ctx.timeZone);

  // `GET /bills` cannot filter by due date and takes one paymentStatus, so two reads of the newest pages (API gap 1).
  const q = useQuery({
    queryKey: ["dues", ...scopeKey],
    queryFn: async () => {
      const [unpaid, partial] = await Promise.all(["unpaid", "partial"].map((paymentStatus) => listBills(client, { paymentStatus, status: "issued", limit: CAP })));
      const rows = [...unpaid!.bills, ...partial!.bills].filter((b) => b.balance > 0);
      rows.sort((a, b) => dueOf(a).localeCompare(dueOf(b)) || a.id.localeCompare(b.id));
      return { rows, capped: unpaid!.nextCursor !== null || partial!.nextCursor !== null };
    },
    enabled: areaAllows("dues", ctx.has), retry: false,
  });

  async function whatsapp(b: BillSummary) {
    const w = window.open("", "_blank");
    try {
      const r = await client.get<{ member?: { phone?: string | null }; phone?: string | null }>(`/members/${b.memberId}`);
      const phone = r.member?.phone ?? r.phone;
      if (isUnmasked(phone) && w) w.location.href = whatsappHref(phone);
      else { w?.close(); toast.show("No phone number available"); }
    } catch (e) { w?.close(); toast.show(isApiError(e) ? e.message : billing.errors.generic); }
  }

  if (ctx.status !== "loading" && !areaAllows("dues", ctx.has)) return <NoAccess />;
  // The owed amount is `billing.dues` (legacy billing:view + see_revenue, exactly what showed it before).
  const rev = ctx.has("billing.dues");
  // WhatsApp reads the member (GET /members/:id) for an unmasked phone: both levers, or the button only fails.
  const canWhatsapp = ctx.has("members.see_phone") && ctx.has("members.view");
  const groups = ORDER.map((k) => ({ k, rows: (q.data?.rows ?? []).filter((b) => bucketOf(dueOf(b), day) === k) })).filter((g) => g.rows.length > 0);
  return (
    <div className={s.page}>
      <title>Dues · Helmr</title>
      <PageHead title={t.title} subtitle={t.subtitle} />
      <Card>
        {q.isPending ? <div className={s.skel} aria-busy="true" aria-label="Loading dues">{[0, 1, 2, 3].map((i) => <Skeleton key={i} height={46} />)}</div>
          : q.error ? <QueryFailure error={q.error} what="dues" onRetry={() => void q.refetch()} />
          : q.data.rows.length === 0 ? <EmptyState icon="wallet" title={t.empty} description={t.emptyBody} />
          : (
            <>
              {q.data.capped ? <p className={s.notice}>{t.capped}</p> : null}
              {groups.map((g) => (
                <section key={g.k} aria-label={t.buckets[g.k]}>
                  <div className={s.bucket}>{t.buckets[g.k]} · {g.rows.length}</div>
                  {g.rows.map((b) => {
                    const name = b.buyerName ?? "Walk-in";
                    const late = daysBetween(dueOf(b), day);
                    return (
                      <div key={b.id} className={s.dueRow}>
                        <div>
                          <div className={s.nm}>{name}</div>
                          <div className={s.muted}>{formatDate(dueOf(b), ctx.locale, "short")}{late > 0 ? ` · ${t.overdueBy(late)}` : ""}{rev ? <> · <span className={s.num}>{t.owed} {fmt(b.balance)}</span></> : null}</div>
                        </div>
                        <div className={s.dueBtns}>
                          {ctx.has("billing.collect_payment") ? <Button as={Link} to={`/billing/bills/${b.id}`} size="sm" variant="primary" aria-label={t.collectLabel(name)}>{t.collect}</Button> : null}
                          {canWhatsapp ? <Button size="sm" aria-label={t.waLabel(name)} onClick={() => void whatsapp(b)}><Icon name="chat" />{t.whatsapp}</Button> : null}
                        </div>
                      </div>
                    );
                  })}
                </section>
              ))}
            </>
          )}
      </Card>
    </div>
  );
}
